import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { DocumentViewer } from '@repo/components';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { Spinner } from '@repo/components';
import { GeneralNotFound } from '@repo/components';
import { getPartnerCompanyUserDocuments } from 'src/api/partner';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/users/$userId/$documentId')({
	component: RouteComponent,
	validateSearch: (search: {
		search: string;
		roleIds: string;
		page: number;
		sort: string;
		roleStatus: string;
		activeUserId: string;
	}) => ({
		// These need to be defined here to access them in this route
		search: (search.search as string) || '',
		roleIds: (search.roleIds as string) || '',
		page: Number(search.page) || 0,
		sort: (search.sort as string) || 'updatedAt:desc',
		roleStatus: (search.roleStatus as string) || 'all',
		activeUserId: (search.activeUserId as string) || '',
	}),
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, userId, documentId } = Route.useParams();
	const parentSearch = Route.useSearch();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	useBreadcrumbLevel(1, 'Users', `/partner/${partnerId}/users`);
	useBreadcrumbLevel(2, 'Document Details');

	const { data: userDocumentResponse, isLoading: userDocumentsLoading } = useQuery({
		queryKey: ['partner-company-user-documents', partnerId, userId, documentId],
		queryFn: () => getPartnerCompanyUserDocuments(partnerId, userId, 'includeLogs=true'),
	});
	const userDocument = userDocumentResponse?.data?.find((document) => document.id === documentId);

	if (userLoading || userDocumentsLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (!userDocument) {
		return (
			<GeneralNotFound
				label="We couldn't find this document. Maybe it has been deleted?"
				action={{
					label: 'Return to documents',
					href: `/partner_/${partnerId}/users`,
					variant: 'outlined',
				}}
			/>
		);
	}

	const handleRevalidateDocument = async () => {
		// Invalidate this specific document
		await queryClient.invalidateQueries({
			queryKey: ['partner-company-user-document', partnerId, userId, documentId],
		});
	};

	const handleClose = () => {
		navigate({
			to: `/partner/$partnerId/users`,
			params: { partnerId },
			search: {
				...parentSearch,
				activeUserId: userId, // This will reopen the sheet for this user
			},
		});
		return null;
	};

	return (
		<DocumentViewer
			document={userDocument}
			onClose={handleClose}
			types={[]} // You might want to get document types from API
			apiUrlBase={`/api/partner-portal/groups/${partnerId}/users/${userId}/files`}
			onRevalidateDocument={handleRevalidateDocument}
			tasks={[]}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.partnerCompanyUsersView]),
				fullView: verifyPermission([PERMISSIONS.partnerCompanyUsersView]),
				sensitiveView: false,
				edit: false,
				archive: false,
				review: false,
				sensitiveEdit: false,
				sensitiveArchive: false,
			}}
		/>
	);
}
